import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu, X, KeyRound, Sun, Moon, Phone, ChevronDown } from 'lucide-react';
import { useTheme } from '../lib/theme.jsx';
import { SERVICES } from '../lib/site';

const HEADER_VARS = {
    '--cream': '242 241 250',
    '--fog': '178 179 205',
    '--bronze': '154 160 245',
    '--line': '50 44 94',
};

const LINKS = [
    { to: '/', label: 'Home', testId: 'nav-home-link' },
    { to: '/about', label: 'About', testId: 'nav-about-link' },
    { to: '/services', label: 'Services', testId: 'nav-services-link' },
    { to: '/events', label: 'Events', testId: 'nav-events-link' },
    { to: '/media', label: 'Media', testId: 'nav-media-link' },
    { to: '/contact', label: 'Contact', testId: 'nav-contact-link' },
];

export default function NavigationHeader() {
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const { theme, toggle } = useTheme();

    useEffect(() => setOpen(false), [location.pathname]);

    return (
        <header
            data-testid="nav-header"
            style={HEADER_VARS}
            className="fixed top-0 inset-x-0 z-50 bg-[#15132B] border-b border-white/10"
        >
            <div className="max-w-7xl mx-auto px-5 sm:px-8 relative flex items-center justify-between h-[4.5rem]">
                <Link to="/" data-testid="nav-logo" aria-label="CommPro Worldwide home">
                    <img src="/assets/commpro-logo-white.png" alt="CommPro Worldwide" className="h-[50px] w-auto" />
                </Link>

                <nav className="hidden lg:flex items-center gap-8 absolute left-1/2 -translate-x-1/2">
                    {LINKS.map((l) =>
                        l.to === '/services' ? (
                            <div key={l.to} className="relative group">
                                <NavLink
                                    to={l.to}
                                    data-testid={l.testId}
                                    className={({ isActive }) =>
                                        `inline-flex items-center gap-1 text-[15px] font-medium transition-colors duration-300 ${
                                            isActive ? 'text-white' : 'text-white/60 hover:text-white'
                                        }`
                                    }
                                >
                                    {l.label}
                                    <ChevronDown size={13} className="group-hover:rotate-180 transition-transform duration-300" />
                                </NavLink>
                                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 opacity-0 invisible translate-y-2 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-300">
                                    <div
                                        data-testid="nav-services-dropdown"
                                        className="bg-[#1B1740] border border-white/10 rounded-xl p-3 w-80 shadow-2xl"
                                    >
                                        {SERVICES.map((s) => (
                                            <Link
                                                key={s.id}
                                                to="/services"
                                                data-testid={`nav-dropdown-${s.id}`}
                                                className="block px-4 py-2.5 text-sm text-white/65 hover:text-white hover:bg-white/5 rounded-lg transition-colors duration-200"
                                            >
                                                {s.title}
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                data-testid={l.testId}
                                className={({ isActive }) =>
                                    `text-[15px] font-medium transition-colors duration-300 ${
                                        isActive ? 'text-white' : 'text-white/60 hover:text-white'
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        )
                    )}
                </nav>

                <div className="hidden lg:flex items-center gap-5">
                    <button
                        data-testid="theme-toggle"
                        onClick={toggle}
                        aria-label="Toggle light and dark theme"
                        className="text-white/60 hover:text-white transition-colors duration-300"
                    >
                        {theme === 'dark' ? <Sun size={16} strokeWidth={1.5} /> : <Moon size={16} strokeWidth={1.5} />}
                    </button>
                    <Link
                        to="/admin"
                        data-testid="nav-admin-link"
                        aria-label="Admin"
                        className="text-white/60 hover:text-white transition-colors duration-300"
                    >
                        <KeyRound size={16} strokeWidth={1.5} />
                    </Link>
                    <a
                        href="tel:+12125550184"
                        data-testid="nav-phone-link"
                        className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-white transition-colors duration-300"
                    >
                        <Phone size={14} strokeWidth={1.5} />
                        212.555.0184
                    </a>
                    <Link
                        to="/contact"
                        data-testid="nav-cta-button"
                        className="bg-white text-[#15132B] text-sm font-semibold px-6 py-2.5 rounded-full hover:bg-[#9AA0F5] hover:text-white transition-colors duration-300"
                    >
                        Get in touch
                    </Link>
                </div>

                <div className="lg:hidden flex items-center gap-2">
                    <button
                        data-testid="theme-toggle-mobile"
                        onClick={toggle}
                        aria-label="Toggle light and dark theme"
                        className="text-white p-2"
                    >
                        {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
                    </button>
                    <button
                        data-testid="nav-mobile-toggle"
                        onClick={() => setOpen(!open)}
                        className="text-white p-2"
                        aria-label="Toggle menu"
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:hidden overflow-hidden border-t border-white/10 bg-[#15132B]"
                    >
                        <div className="px-6 py-6 flex flex-col gap-5">
                            {LINKS.map((l, i) => (
                                <NavLink
                                    key={l.to}
                                    to={l.to}
                                    data-testid={`${l.testId}-mobile`}
                                    className={({ isActive }) =>
                                        `font-serif text-2xl font-bold ${isActive ? 'text-[#9AA0F5]' : 'text-white'}`
                                    }
                                    style={{ transitionDelay: `${i * 30}ms` }}
                                >
                                    {l.label}
                                </NavLink>
                            ))}
                            <a
                                href="tel:+12125550184"
                                data-testid="nav-phone-link-mobile"
                                className="inline-flex items-center gap-2 text-sm text-white/70"
                            >
                                <Phone size={14} /> 212.555.0184
                            </a>
                            <Link
                                to="/contact"
                                data-testid="nav-cta-button-mobile"
                                className="mt-2 bg-white text-[#15132B] text-sm font-semibold px-6 py-3 rounded-full text-center"
                            >
                                Get in touch
                            </Link>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
}
